import { useState, useEffect, useCallback, useMemo } from "react";
import * as Tone from "tone";
import { progressions } from "../../../utils/chordUtils";
import {
  ChordProgression,
  ChordMapping,
  ChordBarOption,
  SequencerProgressionValue,
} from "../types";
import { DEFAULT_PROGRESSION, DEFAULT_KEY } from "../constants";
import { SequencerMode } from "../components/ModePicker";
import {
  Register,
  getKeyBasedRegister,
  hasNinthInProgression,
  getRegisterHeight,
  adjustRegister as adjustRegisterUtil,
} from "../../../utils/registerVoicing";
import { useRegister } from "../../../context/RegisterContext";

export const useChordProgression = () => {
  // New state for chord progression
  const [selectedKey, setSelectedKey] = useState<string>(DEFAULT_KEY);
  const [selectedMode, setSelectedMode] = useState<"minor" | "major">("minor");
  const [progression, setProgression] =
    useState<ChordProgression>(DEFAULT_PROGRESSION);

  // New state for dynamic chord mapping
  const [chordMapping, setChordMapping] = useState<ChordMapping>({});

  // Drone mode state
  const [sequencerMode, setSequencerMode] = useState<SequencerMode>("chord");
  const [droneNotes, setDroneNotes] = useState<string[]>([]);

  // Ribbon positions state for InteractiveChordProgression (percentage-based)
  const [ribbonPositions, setRibbonPositions] = useState<
    Array<{ startPercent: number; endPercent: number }>
  >([]);

  // Chord bars state (how many bars the chord sequence spans)
  const [chordBars, setChordBars] = useState<ChordBarOption>(4);

  // Initialize register from progression's defaultRegister or key-based
  const initializeRegister = useCallback(
    (prog: ChordProgression, key: string): Register => {
      // Find the progression object to get defaultRegister
      const progressionName = Object.keys(progressions).find(
        (name) =>
          JSON.stringify(progressions[name as keyof typeof progressions]) ===
          JSON.stringify(prog)
      );

      let register: Register;
      if (progressionName) {
        const progObj =
          progressions[progressionName as keyof typeof progressions];
        if (progObj.defaultRegister) {
          register = progObj.defaultRegister;
        } else {
          register = getKeyBasedRegister(key);
        }
      } else {
        register = getKeyBasedRegister(key);
      }

      // Check if progression has 9th and adjust height to 14 semitones if needed
      const hasNinth = hasNinthInProgression(prog);
      if (hasNinth) {
        const currentHeight = getRegisterHeight(register);
        if (currentHeight < 14) {
          // Expand register to 14 semitones by moving top note up
          const bottomMidi = Tone.Frequency(register.bottomNote).toMidi();
          const newTopMidi = bottomMidi + 14;
          register = {
            bottomNote: register.bottomNote,
            topNote: Tone.Frequency(newTopMidi, "midi").toNote(),
          };
        }
      }

      return register;
    },
    []
  );

  // Get register context to sync adjustments
  const { register: contextRegister, setRegister: setContextRegister } =
    useRegister();

  const computedRegister = useMemo(
    () => initializeRegister(progression, selectedKey),
    [initializeRegister, progression, selectedKey]
  );

  const progressionName = useMemo(() => {
    return (
      Object.keys(progressions).find((name) => {
        const candidate = progressions[name as keyof typeof progressions];
        if (candidate.mode !== progression.mode) {
          return false;
        }
        if (candidate.roman.length !== progression.roman.length) {
          return false;
        }
        return candidate.roman.every(
          (roman, index) => roman === progression.roman[index]
        );
      }) || ""
    );
  }, [progression]);

  const register = contextRegister ?? computedRegister;

  // Filter progressions by selected mode
  const filteredProgressions = useMemo(() => {
    return Object.fromEntries(
      Object.entries(progressions).filter(
        ([, prog]) => prog.mode === selectedMode
      )
    );
  }, [selectedMode]);

  // Sync selectedMode with progression mode when progression changes
  useEffect(() => {
    if (progression.mode !== selectedMode) {
      setSelectedMode(progression.mode);
    }
  }, [progression.mode, selectedMode]);

  // Initialize register context once (or when externally cleared).
  // This avoids resetting on chord edits while still ensuring the context
  // has a usable starting register.
  useEffect(() => {
    if (!contextRegister) {
      setContextRegister(initializeRegister(progression, selectedKey));
    }
  }, [
    contextRegister,
    initializeRegister,
    progression,
    selectedKey,
    setContextRegister,
  ]);

  // Handle mode change
  const handleModeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMode = e.target.value as "minor" | "major";
      setSelectedMode(newMode);

      // Default to the first progression suitable for the new mode
      const firstProgressionName = Object.keys(progressions).find(
        (key) => progressions[key as keyof typeof progressions].mode === newMode
      );

      if (firstProgressionName) {
        const nextProgression =
          progressions[firstProgressionName as keyof typeof progressions];
        setProgression(nextProgression);
      }
    },
    []
  );

  // Handle key change
  const handleKeyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newKey = e.target.value;
      setSelectedKey(newKey);
    },
    []
  );

  // Handle progression change
  const handleProgressionChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement> | string) => {
      // If e is a string, it was called directly with the progression name
      // Otherwise extract the value from the event
      const progressionName = typeof e === "string" ? e : e.target.value;

      if (
        filteredProgressions[
          progressionName as keyof typeof filteredProgressions
        ]
      ) {
        const nextProgression =
          filteredProgressions[
            progressionName as keyof typeof filteredProgressions
          ];
        setProgression(nextProgression);
      }
    },
    [filteredProgressions]
  );

  // Handle chord mapping changes from InteractiveChordProgression
  const handleChordMappingChange = useCallback(
    (newChordMapping: ChordMapping) => {
      setChordMapping(newChordMapping);
    },
    []
  );

  // Handle updating a single chord in the progression
  const handleChordUpdate = useCallback(
    (chordIndex: number, newChord: string) => {
      setProgression((prevProgression) => {
        const updatedRoman = [...prevProgression.roman];
        if (chordIndex >= 0 && chordIndex < updatedRoman.length) {
          updatedRoman[chordIndex] = newChord;
          return {
            ...prevProgression,
            roman: updatedRoman,
          };
        }
        return prevProgression;
      });
    },
    []
  );

  // Handle swapping two chords in the progression
  const handleChordSwap = useCallback(
    (sourceIndex: number, targetIndex: number) => {
      if (sourceIndex === targetIndex) return;

      setProgression((prevProgression) => {
        const nextRoman = [...prevProgression.roman];
        if (
          sourceIndex < 0 ||
          targetIndex < 0 ||
          sourceIndex >= nextRoman.length ||
          targetIndex >= nextRoman.length
        ) {
          return prevProgression;
        }

        [nextRoman[sourceIndex], nextRoman[targetIndex]] = [
          nextRoman[targetIndex],
          nextRoman[sourceIndex],
        ];

        return {
          ...prevProgression,
          roman: nextRoman,
        };
      });
    },
    []
  );

  const setProgressionFromValue = useCallback(
    (nextValue: SequencerProgressionValue) => {
      if (typeof nextValue === "string") {
        const nextProgression = progressions[nextValue as keyof typeof progressions];
        if (nextProgression) {
          setProgression(nextProgression);
        }
        return;
      }

      if (!Array.isArray(nextValue.roman) || nextValue.roman.length === 0) {
        return;
      }

      setSelectedMode(nextValue.mode);
      setProgression({
        mode: nextValue.mode,
        roman: [...nextValue.roman],
      });
    },
    []
  );

  // Adjust register up or down by semitones
  const adjustRegister = useCallback(
    (semitones: number) => {
      const baseRegister = contextRegister ?? computedRegister;
      const newRegister = adjustRegisterUtil(baseRegister, semitones);
      setContextRegister(newRegister);
    },
    [computedRegister, contextRegister, setContextRegister]
  );

  // Handle sequencer mode change
  const handleSequencerModeChange = useCallback((mode: SequencerMode) => {
    setSequencerMode(mode);
  }, []);

  // Handle drone notes change
  const handleDroneNotesChange = useCallback((notes: string[]) => {
    const normalizedNotes = notes.map((note) => note.replace(/-?\d+$/, ""));
    setDroneNotes(normalizedNotes);
  }, []);

  return {
    selectedKey,
    selectedMode,
    progression,
    chordMapping,
    filteredProgressions,
    handleModeChange,
    handleKeyChange,
    handleProgressionChange,
    handleChordMappingChange,
    handleChordUpdate,
    handleChordSwap,
    setSelectedKey,
    setProgressionFromValue,
    progressionName,
    register,
    setRegister: setContextRegister,
    adjustRegister,
    sequencerMode,
    droneNotes,
    handleSequencerModeChange,
    handleDroneNotesChange,
    ribbonPositions,
    setRibbonPositions,
    chordBars,
    setChordBars,
  };
};
